/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { ModalsProvider } from '@mantine/modals';
import React from 'react';
import { Outlet, ScrollRestoration, redirect, type RouteObject } from 'react-router-dom';

import { Products } from '@fai/constants/domain-values';
import { RootErrorBoundary } from '@fai/friendli-suite/features/routes/error-boundaries';
import {
  redirectDefaultTeamToTeamIdPath,
  strictCheckIfIsTeamOwner,
  validateTeamId,
} from '@fai/friendli-suite/features/routes/loaders/client-team';
import { updateIntercomUser } from '@fai/friendli-suite/features/routes/loaders/intercom';
import {
  startContainerTrial,
  checkContainerAuthority,
} from '@fai/friendli-suite/features/routes/loaders/product/container';
import { checkServerlessEndpointsAuthority } from '@fai/friendli-suite/features/routes/loaders/product/serverless-endpoints';
import { redirectToLandingIfPlanInaccessible } from '@fai/friendli-suite/features/routes/loaders/product/serverless-endpoints/check-serverless-endpoints-accessibility';
import { setSentryUser } from '@fai/friendli-suite/features/routes/loaders/sentry';
import { forceSignOut } from '@fai/friendli-suite/features/routes/loaders/session';
import { hideOptionFromRelease } from '@fai/friendli-suite/shared/environments/hide-from-release';
import { SetIntegrationsContext } from '@fai/friendli-suite/shared/integrations';
import AuthLayout from '@fai/friendli-suite/views/layout/auth/auth-layout';
import {
  docsHeaderLinkProps,
  pricingHeaderLinkProps,
  websiteHeaderLinkProps,
} from '@fai/friendli-suite/views/layout/auth/links';
import ProductBasicLayout from '@fai/friendli-suite/views/layout/product/product-basic-layout';
import SettingBasicLayout from '@fai/friendli-suite/views/layout/settings/setting-basic-layout';
import { pipeLoaders } from '@fai/third-party/react-router-dom';
import {
  redirectIfSessionExists,
  redirectIfSessionExpired,
  sessionGateway,
} from '@fai/third-party/supertokens';
import { modals } from '@fai/ui/mantine-modal';

import { authBeforeLoginRoutes, authAfterLoginRoutes, forceSignOutRoutes } from './auth-routes';
import containerRoutes from './container-routes';
import dedicatedEndpointsRoutes from './dedicated-endpoints-routes';
import { invitationRoutes } from './invitation-routes';
import serverlessEndpointsRoutes, {
  serverlessEndpointsModelDetailRoutes,
} from './serverless-endpoints-routes';
import { teamSettingsRoutes } from './team-settings-routes';
import userSettingsRoutes from './user-settings-routes';

const ErrorPage = React.lazy(() => import('@fai/friendli-suite/pages/error'));
const SSORedirectionPage = React.lazy(() => import('@fai/friendli-suite/pages/sso'));
const EnterCodePage = React.lazy(() => import('@fai/friendli-suite/pages/auth/enter-code'));

const SuitePage = React.lazy(() => import('@fai/friendli-suite/pages/suite'));
const MockPage = React.lazy(() => import('@fai/friendli-suite/pages/mock'));
const AuthSignupVerificationPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/signup/verification')
);

const routesConfig: RouteObject[] = [
  // NO SESSION
  {
    loader: redirectIfSessionExists('/default-team'),
    ErrorBoundary: RootErrorBoundary,
    children: [
      // Auth Pages
      ...authBeforeLoginRoutes,
    ],
  },

  // Routes that require a clean session (signed out)
  {
    loader: pipeLoaders({ concurrent: [forceSignOut] }),
    ErrorBoundary: RootErrorBoundary,
    children: forceSignOutRoutes,
  },

  /**
   * INTERMEDIATE FLOW
   * accessible with/without session
   */
  {
    ErrorBoundary: RootErrorBoundary,
    children: [
      {
        path: 'error',
        Component: ErrorPage,
      },
      { path: 'sso', Component: SSORedirectionPage },
      ...hideOptionFromRelease({
        path: 'enter-code',
        Component: () => (
          <AuthLayout
            rightItems={[docsHeaderLinkProps, pricingHeaderLinkProps, websiteHeaderLinkProps]}
          />
        ),
        children: [{ index: true, Component: EnterCodePage }],
      }),
      {
        path: 'signup/verification',
        Component: AuthLayout,
        children: [{ index: true, Component: AuthSignupVerificationPage }],
      },

      /**
       * Get Started
       * when user is redirected from company website
       */
      {
        path: 'get-started/*',
        children: [
          {
            path: 'suite',
            loader: sessionGateway({
              ifSessionPath: '/default-team/suite',
              noSessionPath: `/signup/self-register`,
            }),
          },
          {
            path: 'serverless-endpoints',
            loader: sessionGateway({
              ifSessionPath: '/default-team/check-authority/serverless-endpoints',
              noSessionPath: `/signup/self-register?product=${Products.ServerlessEndpoints}`,
            }),
          },
          {
            path: 'dedicated-endpoints',
            loader: sessionGateway({
              ifSessionPath: '/default-team/dedicated-endpoints',
              noSessionPath: `/signup/self-register?product=${Products.DedicatedEndpoints}`,
            }),
          },
          {
            path: 'container',
            loader: sessionGateway({
              ifSessionPath: '/default-team/check-authority/container',
              noSessionPath: `/signup/self-register?product=${Products.Container}`,
            }),
          },
          {
            path: '*',
            // For now, default product is 'serverless-endpoints'
            loader: () => redirect('serverless-endpoints'),
          },
        ],
      },
    ],
  },

  // INVITATION FLOW
  {
    ErrorBoundary: RootErrorBoundary,
    children: [...invitationRoutes],
  },

  // SESSION REQUIRED
  {
    loader: pipeLoaders({
      sequence: [redirectIfSessionExpired],
      concurrent: [setSentryUser],
    }),
    Component: SetIntegrationsContext,
    ErrorBoundary: RootErrorBoundary,

    children: [
      // Onboarding and Invitation(logged-in) Pages
      ...authAfterLoginRoutes,

      // User Settings
      {
        path: 'user-settings/*',
        loader: pipeLoaders({
          sequence: [redirectIfSessionExpired, updateIntercomUser],
        }),
        Component: SettingBasicLayout,
        children: userSettingsRoutes,
      },

      /**
       * Used when teamId is not provided
       * e.g. when user declines invitation
       * `/default-team/...`: Replace to `/team/{default-team-id}/...`
       */
      {
        path: 'default-team/*',
        loader: pipeLoaders({
          sequence: [redirectIfSessionExpired, redirectDefaultTeamToTeamIdPath],
        }),
      },

      /**
       * TODO: reinforce why this route is needed
       */
      {
        path: 'team',
        loader: () => redirect('/default-team'),
      },

      // Team Pages
      {
        path: 'team/:teamId/*',
        loader: pipeLoaders({
          sequence: [redirectIfSessionExpired],
          concurrent: [validateTeamId, updateIntercomUser],
        }),
        children: [
          /**
           * Redirects to `/team/:teamId/suite`
           *
           * but if plan === 'NONE_MEMBER' || plan === 'ACCESS_REQUIRED'
           * query strings of `?contact=<productName>` is attached
           */
          {
            path: 'check-authority/*',
            children: [
              {
                path: 'serverless-endpoints',
                loader: pipeLoaders({
                  sequence: [redirectIfSessionExpired, checkServerlessEndpointsAuthority],
                }),
              },
              {
                path: 'container',
                loader: pipeLoaders({
                  sequence: [
                    redirectIfSessionExpired,
                    startContainerTrial,
                    checkContainerAuthority,
                  ],
                }),
              },
            ],
          },

          // Team Settings
          {
            path: '*',
            loader: pipeLoaders({
              sequence: [redirectIfSessionExpired, strictCheckIfIsTeamOwner],
            }),
            Component: SettingBasicLayout,
            children: teamSettingsRoutes,
          },

          // Product Pages
          {
            path: '*',
            Component: ProductBasicLayout,
            children: [
              {
                path: 'suite',
                Component: SuitePage,
              },
              {
                path: 'serverless-endpoints',
                children: serverlessEndpointsRoutes,
              },
              {
                path: 'container',
                children: containerRoutes,
              },
              {
                path: '*',
                loader: () => redirect('suite'),
              },
            ],
          },

          // Dedicated Endpoints Pages with new layout
          {
            path: 'dedicated-endpoints',
            children: dedicatedEndpointsRoutes,
          },

          // Serverless Endpoints playgrounds without layout
          {
            path: 'serverless-endpoints/model/*',
            loader: pipeLoaders({
              sequence: [redirectIfSessionExpired, redirectToLandingIfPlanInaccessible],
            }),
            children: serverlessEndpointsModelDetailRoutes,
          },
        ],
      },
    ],
  },

  // Mock
  ...hideOptionFromRelease({ path: 'mock', Component: MockPage }),

  // default is login page
  {
    index: true,
    loader: () => redirect('login'),
  },
  {
    path: '*',
    loader: () => redirect('login'),
  },
];

const rootRoutesConfig: RouteObject[] = [
  {
    element: (
      /**
       * Some modals have navigation links inside,
       * so this component cannot be placed in main.tsx or app.tsx
       *
       * rootRoutesConfig is just a array of specifications used as
       * parameter of `createBrowserRouter()`.
       * If ModalsProvider is executed prior to createBrowserRouter,
       * navigation links inside cannot access to react-router-dom's history object
       */
      <ModalsProvider modals={modals}>
        <Outlet />
        <ScrollRestoration />
      </ModalsProvider>
    ),
    children: routesConfig,
  },
];

export default rootRoutesConfig;
